import { useEffect, useCallback, useRef } from 'preact/hooks'
import { useForm, FieldValues, UseFormProps, Path, PathValue } from 'react-hook-form'
import { useAIChatContextUpdater } from './useAIChat'
import { fieldUpdateHandler as fieldUpdateHandlerSignal } from '@/state/aiChat'
import type { AIConversationContext, FieldUpdateHandler, ContextualField } from '@/types/aiChat'

/**
 * Configuration for a single AI-editable field
 */
export interface AIFieldConfig {
  /** AI field name (e.g., 'downloadTitle') */
  aiName: string
  /** react-hook-form field path (e.g., 'title') */
  formPath: string
  /** Human-readable label for AI context */
  displayName: string
  /** Field input type */
  fieldType: ContextualField['fieldType']
  /** Maximum character length */
  maxLength?: number
  /** Whether AI can modify this field (default: true) */
  isEditable?: boolean
  /** If true, value is serialized as JSON array of .value strings for AI context */
  isListField?: boolean
}

/**
 * AI form configuration
 */
export interface AIFormConfig {
  /** AI Chat context configuration (without fields/actions — those are auto-built) */
  aiContext: Omit<AIConversationContext, 'availableFields' | 'availableActions'>
  /** Field configurations for auto-sync and field handler */
  fields: AIFieldConfig[]
  /** Additional loaded data passed to AI context (e.g., speaker info) */
  loadedData?: Record<string, unknown>
  /** When true, skips context sync and handler registration (e.g., when a child modal owns context) */
  contextSuppressed?: boolean
  /** Called after AI updates a field — use for list field routing or custom logic */
  onFieldUpdate?: (aiFieldName: string, value: string) => void | Promise<void>
  /** Whether this is an edit form (affects button labels and dirty checking) */
  isEditMode?: boolean
}

export interface FormButtonState {
  /** Whether the save button should be disabled */
  isSaveDisabled: boolean
  /** Suggested save button text based on form state */
  saveText: string
  /** Reason why save is disabled (for debugging/tooltips) */
  disabledReason?: string
}

/**
 * Strips the list markup Webster writes around list content — leading bullet or number markers
 * and markdown emphasis — leaving the bare text that belongs in the field.
 *
 * Order matters: the bullet rule runs before emphasis is removed, so a `**Bold**: text` line isn't
 * mistaken for a `*` bullet (a real bullet always has whitespace after the marker).
 */
function stripListMarkup(line: string): string {
  return line
    .trim()
    .replace(/^[-*•]\s+/, '')
    .replace(/^\d+[.)]\s+/, '')
    .replace(/\*\*/g, '')
    .trim()
}

/**
 * Utility for handling AI list field updates. Every path here APPENDS — a JSON array appends one
 * item per element, anything else appends one item per line. Nothing is ever cleared or replaced,
 * and that is deliberate: the common flow is a user writing an item or two themselves and then
 * asking Webster for a few more, which only works if applying a suggestion adds to what is already
 * there. (Deciding append-vs-replace from the user's intent is a separate, unbuilt idea.)
 *
 * The line split is the part that matters. A suggestion for a list field routinely arrives as
 * several lines inside one string — ask for three agenda items and Webster answers with numbered
 * options whose bodies are three lines each, and the server-side variation parser keeps those
 * lines together because for a scalar field (an overview, a title) that genuinely IS one value.
 * Appending the block unsplit is how an agenda ended up as a single run-on item with its line
 * breaks eaten (`...in B2B branding.Expert Panel: Operationalizing...`), long enough to fail the
 * 200-character per-item validator with a 422 (ORB-475). One line, one item.
 */
export function handleListFieldUpdate(
  value: string,
  append: (item: { value: string }) => void,
) {
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) {
      for (const v of parsed) {
        const text = String(v).trim()
        if (text) append({ value: text })
      }
      return
    }
  } catch { /* not JSON — fall through to the line split */ }

  for (const line of value.split('\n')) {
    const item = stripListMarkup(line)
    if (item) append({ value: item })
  }
}

/**
 * Hook that combines react-hook-form with AI Chat integration.
 * Creates the form, auto-syncs AI context from field configs, and registers the field update handler.
 *
 * @example
 * const { form, getFormButtonState } = useAIForm<CampaignFormData>({
 *   mode: 'onChange',
 *   defaultValues: { Name: '', Description: '' }
 * }, {
 *   aiContext: {
 *     location: ChatLocations.CampaignEditor,
 *     viewMode: ChatViewModes.Edit,
 *   },
 *   fields: [
 *     { aiName: 'campaignName', formPath: 'Name', displayName: 'Campaign Name', fieldType: 'text', maxLength: 200 },
 *     { aiName: 'campaignDescription', formPath: 'Description', displayName: 'Description', fieldType: 'textarea', maxLength: 5000 },
 *   ],
 *   isEditMode: true,
 * })
 */
export function useAIForm<TFieldValues extends FieldValues = FieldValues>(
  formOptions: UseFormProps<TFieldValues>,
  aiConfig: AIFormConfig
) {
  const form = useForm<TFieldValues>(formOptions)
  const { setFieldUpdateHandler, updateContext } = useAIChatContextUpdater()

  const { setValue, getValues, trigger, formState, watch } = form
  const { fields, onFieldUpdate, isEditMode = false, contextSuppressed, loadedData } = aiConfig
  const { isValid, isDirty } = formState

  // Use refs for callbacks to avoid effect re-runs on every render
  const onFieldUpdateRef = useRef(onFieldUpdate)
  onFieldUpdateRef.current = onFieldUpdate

  const fieldsRef = useRef(fields)
  fieldsRef.current = fields

  // Watch all form values to trigger context re-sync
  const watchedValues = watch()

  // Auto-sync AI context whenever form values or config changes
  useEffect(() => {
    if (contextSuppressed) return

    const currentFields = fieldsRef.current
    const contextFields: ContextualField[] = currentFields.map(f => {
      let currentValue: string
      if (f.isListField) {
        const listValue = form.getValues(f.formPath as Path<TFieldValues>)
        const items = Array.isArray(listValue)
          ? listValue.map((item: { value?: string }) => ((item?.value ?? '') as string).trim()).filter(Boolean)
          : []
        currentValue = JSON.stringify(items)
      } else {
        // An object-valued field, such as the Quiz tab's working quiz, reaches Webster as JSON rather than
        // "[object Object]".
        const raw: unknown = form.getValues(f.formPath as Path<TFieldValues>)
        currentValue = raw !== null && typeof raw === 'object' ? JSON.stringify(raw) : String(raw ?? '')
      }

      return {
        name: f.aiName,
        displayName: f.displayName,
        fieldType: f.fieldType,
        currentValue,
        isEditable: f.isEditable ?? true,
        isFocused: false,
        maxLength: f.maxLength,
        isListField: f.isListField,
      }
    })

    const fullContext = {
      ...aiConfig.aiContext,
      availableFields: contextFields,
      availableActions: ['ApplyToField', 'CopyToClipboard'],
      loadedData,
    }

    updateContext(fullContext)
  }, [watchedValues, contextSuppressed, fields, loadedData, updateContext, aiConfig.aiContext])

  // Register field update handler
  useEffect(() => {
    if (contextSuppressed) return

    const handler: FieldUpdateHandler = async (fieldName: string, value: string) => {
      const currentFields = fieldsRef.current
      // Match the aiName exactly, then fall back to matching after lowering
      // fieldName's first character. Webster's get_field_limits tool returns
      // PascalCase FieldKey identifiers (e.g. "DownloadTitle"), and the model
      // may reference those in its response when calling ApplyToField. The
      // bridge is intentionally temporary — it retires once aiName itself
      // migrates to PascalCase in a future unification ticket.
      const fieldConfig = currentFields.find(f =>
        f.aiName === fieldName ||
        f.aiName === (fieldName[0]?.toLowerCase() ?? '') + fieldName.slice(1)
      )

      if (!fieldConfig) {
        console.warn('[useAIForm] Unknown AI field name:', fieldName)
        return
      }

      // List fields are backed by useFieldArray (an array of { value }). Calling
      // setValue on the array path with the raw string would corrupt it into a
      // string, breaking every consumer that iterates it. Route those entirely
      // through onFieldUpdate (which appends via the field array's append()).
      if (fieldConfig.isListField) {
        if (onFieldUpdateRef.current) {
          await onFieldUpdateRef.current(fieldName, value)
        }
        return
      }

      // An object-valued field (the Quiz tab's working quiz) would be overwritten by the string. Its value is
      // JSON the consumer merges itself, so it goes to onFieldUpdate as it came.
      const current: unknown = getValues(fieldConfig.formPath as Path<TFieldValues>)
      if (current !== null && typeof current === 'object') {
        if (onFieldUpdateRef.current) {
          await onFieldUpdateRef.current(fieldName, value)
        }
        return
      }

      // Set the value on the form
      setValue(
        fieldConfig.formPath as Path<TFieldValues>,
        value as PathValue<TFieldValues, Path<TFieldValues>>,
        { shouldValidate: true, shouldDirty: true, shouldTouch: true }
      )

      await trigger(fieldConfig.formPath as Path<TFieldValues>)

      // Call the consumer's onFieldUpdate for custom logic (e.g., list field routing)
      if (onFieldUpdateRef.current) {
        await onFieldUpdateRef.current(fieldName, value)
      }
    }

    setFieldUpdateHandler(handler)

    return () => {
      // Only unregister if we're still the current handler — prevents a parent's
      // cleanup from nulling out a child's handler during effect ordering.
      if (fieldUpdateHandlerSignal.value === handler) {
        setFieldUpdateHandler(null)
      }
    }
  }, [contextSuppressed, setValue, getValues, trigger, setFieldUpdateHandler])

  /**
   * Calculate the standardized button state for forms
   */
  const getFormButtonState = useCallback(
    (isSubmitting: boolean, hasExternalChanges?: boolean): FormButtonState => {
      if (isSubmitting) {
        return {
          isSaveDisabled: true,
          saveText: 'Saving...',
          disabledReason: 'Form is submitting'
        }
      }

      if (!isValid) {
        return {
          isSaveDisabled: true,
          saveText: isEditMode ? 'Save Changes' : 'Save',
          disabledReason: 'Form validation failed'
        }
      }

      if (isEditMode && !isDirty && !hasExternalChanges) {
        return {
          isSaveDisabled: true,
          saveText: 'No Changes',
          disabledReason: 'No changes to save'
        }
      }

      return {
        isSaveDisabled: false,
        saveText: isEditMode ? 'Save Changes' : 'Save'
      }
    },
    [isValid, isDirty, isEditMode]
  )

  return {
    form,
    getFormButtonState,
  }
}
